import axios from 'axios';
import { R7E_DebugManager } from './r7e_debug_manager';
import { R7E_CacheManager } from './r7e_cache_manager';
import { R7E_CmsAuth, LocalStoragePrefix } from './cms/r7e_cms';

import { r7eSettings } from './r7e_settings';

// --- Constants ---
const API_URL = process.env.VUE_APP_API_CMS_URL + process.env.VUE_APP_API_R7E_VERSION + '/';
const DEBUG_MODE = process.env.VUE_APP_DEBUG_MODE === "true";

// --- Custom Error for Account Type Issues ---
/**
 * Custom error for account type mismatches.
 */
class R7eAccountTypeError extends Error {
    /**
     * @param {string} message - Error message.
     * @param {Array<string>} a_roles - Roles associated with the account.
     */
    constructor(message, a_roles = []) {
        super(message);
        this.name = 'R7eAccountTypeError';
        this.roles = a_roles;
    }
}


class R7E_NavBarData {
    constructor(app, title_id) {
        this.app = app;
        this._title_id = title_id;
    }
    setTitleId(title_id) { this._title_id = title_id; }
    setItemsBuilder(itemsBuilder) { this.itemsBuilder = itemsBuilder; }

    title_id() { return this._title_id; }
    title() { return null; }
    items() { 
        return this.itemsBuilder ? this.itemsBuilder(this) : []; 
    }
    description() { return null; }
    icon() { return null; }
    // condition() { return null; }
}

class R7E_HomeItem {
    constructor(app,title, description, link) {
        this.app = app;
        this.title = title;
        this.description = description;
        this.link = link;

        this.icon = null;
        this.image = null;
        this.svg = null;
        this.css_class = null;
        this.condition = null;
    }
    setIcon(icon) { this.icon = icon; }
    setCSSClass(css_class) { this.css_class = css_class; }
    setImage(image) { this.image = image; }
    setSVG(svg) { this.svg = svg; }
    setCondition(condition) { this.condition = condition; }
}

/**
 * R7E_IBaseApp
 *
 * Shared base implementation for Recognyze applications, parameterized by an auth provider.
 * Concrete variants (CMS, Domain) provide the auth implementation.
 */

class R7E_IBaseApp {
    account_type = '';
    auth;
    apiR7eClient = axios.create({ baseURL: API_URL });
    baseAppType = null;
    mainApp = null;
    debugManager = null;
    cacheManager = null;

    /**
     * @param {Object} authImpl - Authentication implementation providing required methods/properties
     */
    constructor(authImpl) {
        if (!authImpl) {
            throw new Error('R7E_IBaseApp requires an auth implementation instance.');
        }
        this.auth = authImpl;
        if (DEBUG_MODE) {
            this.debugManager = new R7E_DebugManager(true);
        }
        this.cacheManager = new R7E_CacheManager();
        // --- Axios Interceptors for Auth ---
        // Attach Bearer token to requests
        this.apiR7eClient.interceptors.request.use(
            config => {
                const token = this.auth.getToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                // console.log('R7E_IBaseApp, request', config);
                return config;
            },
            error => Promise.reject(error)
        );

        // Handle 401s and token refresh
        this.apiR7eClient.interceptors.response.use(
            response => response,
            async error => {
                const originalRequest = error.config;
                const refreshKey = this.auth.getRefreshKey();
                if (
                    error.response &&
                    error.response.status === 401 &&
                    !originalRequest._retry &&
                    refreshKey
                ) {
                    originalRequest._retry = true;
                    try {
                        const newToken = await this.auth.refreshToken();
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        // console.log('R7E_IBaseApp, response', originalRequest);
                        return this.apiR7eClient(originalRequest);
                    } catch (refreshError) {
                        await this.auth.clearCredentials();
                        return Promise.reject(refreshError);
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    setMainApp(app) { this.mainApp = app; }
    getMainApp() { return this.mainApp; }

    setUILayout(layout) { 
        localStorage.setItem('r7e.ui.layout', layout);
    }
    getUILayout() { 
        return localStorage.getItem('r7e.ui.layout') || 'sidebar'; 
    }

    getCacheManager() { return this.cacheManager; }
    getDebugManager() { return this.debugManager; }
    isDebugMode() { return this.debugManager ? this.debugManager.isEnabled() : false; }
    isConsoleLogEnabled() { return this.debugManager ? this.debugManager.isConsoleLogEnabled() : false; }
    setConsoleLogEnabled(b) { if (this.debugManager) { this.debugManager.setConsoleLogEnabled(b); } }
    isUIDebugEnabled() { return this.debugManager ? this.debugManager.isUIDebugEnabled() : false; }
    setUIDebugEnabled(b) { if (this.debugManager) { this.debugManager.setUIDebugEnabled(b); } }
    isDebugModeVisible() { return this.debugManager ? this.debugManager.isVisible() : false; }
    setDebugModeVisible(b) { if (this.debugManager) { this.debugManager.setVisible(b); } }
    isUsingFakeData() { return this.debugManager ? this.debugManager.isUsingFakeData() : false; }
    setUsingFakeData(b) { if (this.debugManager) { this.debugManager.setUsingFakeData(b); } }

    isHelpEnabled() { 
        try {
            const b = localStorage.getItem('r7e.help.enabled');
            if (b === null) {
                return true;
            }
            return b === 'true'; 
        } catch (e) {
            return true;
        }   
    }
    setHelpEnabled(b) { 
        return localStorage.setItem('r7e.help.enabled', b ? 'true' : 'false');
    }


    // --- Role and Access Methods ---
    hasAuthorRole() {
        return this.has_role('author') || this.has_role('publisher');
    }
    hasPublisherRole() {
        return this.has_role('publisher');
    }
    /**
     * Checks if the current user has a specific role.
     * @param {string} a_role 
     * @returns {boolean}
     */
    has_role(role) {
        return this.auth.has_role(role);
    }

    /**
     * Checks if a service is available by attempting to fetch its root URL with fetch() in 'no-cors' mode.
     * This does NOT guarantee a 200 response, only that the server is reachable at the network level.
     * @param {string} service 
     * @returns {Promise<boolean>}
     */
    hasAccessToService(service) {
        return this.auth.hasAccessToService(service);
    }

    hasAccessToCurrentService() {
        return this.hasAccessToService(this.account_type);
    }

    // --- Authentication Methods ---
    registerCredentialsObserver(cb) { return this.auth.registerCredentialsObserver(cb); }
    unregisterCredentialsObserver(cb) { return this.auth.unregisterCredentialsObserver(cb); }
    decodeJWT(token) { return this.auth.decodeJWT(token); }
    scheduleTokenRefresh() { return this.auth.scheduleTokenRefresh(); }
    async login(username, password, condition = null) { return this.auth.login(username, password, this.account_type, condition); }
    async logout() { await this.clearCredentials(); }
    async getAccountInfoFromResponseLinks(response) { return this.auth.getAccountInfoFromResponseLinks(response); }
    async refreshTokenIfNeeded() { return this.auth.refreshTokenIfNeeded(); }
    async refreshToken() { return this.auth.refreshToken(); }
    async revokeToken() { return this.auth.revokeToken(); }
    loadCredentials() { return this.auth.loadCredentials(); }
    // clearCredentialsSync() { return this.auth.clearCredentialsSync(); }
    async clearCredentials() { return this.auth.clearCredentials(); }
    getCurrentService() { return this.mainApp ? this.mainApp.account_type : this.account_type; }
    getUsername() { return this.auth.getUsername(); }
    getDisplayName() { return this.auth.getDisplayName(); }
    getProfileName() { return this.auth.getDisplayName(); }
    getRoles() { return this.auth.getRoles(); }
    getToken() { return this.auth.getToken(); }
    getRefreshKey() { return this.auth.getRefreshKey(); }
    getUserid() { return this.auth.getUserid(); }
    getPendingRegistrations() { return this.auth.getPendingRegistrations(); }
    getAuthData() { return this.auth.getAuthData(); }

    isValidUrl(url) {
        try {
          new URL(url);
          return true;
        } catch {
          return false;
        }
      }
    isValidEmail(email) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(email);
    }
    formatAmount (amount, currency, precision = 2) {
        let result = '';  
        if (typeof amount === 'number') {
          result = amount.toFixed(precision);
        } else {
          result = Number(amount).toFixed(precision);
        }
        if (currency) {
          const currencyLowerCase = currency.toLowerCase();
          if (currencyLowerCase == 'usd') {
            result += " $";
          } else if (currencyLowerCase == 'eur') {
            result += " €";
          } else if (currencyLowerCase == 'gbp') {
            result += " £";
          } else {
            result += " " + currency;
          }
        }
        return result;
    }
    formatNumber (n) {
        if (typeof n !== 'number') return '0';
        return n.toLocaleString('en-US', { useGrouping: true }).replace(/,/g, ' ');
    }
      
    formatFloat (n, precision = 2) {
        if (typeof n !== 'number') return '0.00';
        return n.toFixed(precision);
    }
      
    formatDateTime (dateString) {
        if (!dateString) return 'N/A';
        try {
          return new Date(dateString).toLocaleString();
        } catch {
          return dateString;
        }
    }    

    /**
     * Get the current account's data.
     * @returns {Promise<Object|null>}
     */
    async getAccount() {
        // console.log('R7E_IBaseApp, getAccount');
        await this.auth.refreshTokenIfNeeded();

        try {
            const response = await this.apiR7eClient.get(`account/${this.account_type}`);
            // console.log('R7E_IBaseApp, getAccount response', response);
            let data = response.data;
            let userData = await this.auth.getAccountInfoFromResponseLinks(data);
            if (userData) {
                Object.assign(data, userData);
            }
            return data;
        } catch (error) {
            console.error('Error fetching account:', error.response ? error.response.data : error.message, error);
            return null;
        }
    }

    async getProfile() {
        await this.auth.refreshTokenIfNeeded();
        try {
            const response = await this.apiR7eClient.get(`account/${this.account_type}/profile/`);
            return response.data;
        } catch (error) {
            console.error('Error fetching profile:', error.response ? error.response.data : error.message);
            return null;
        }
    }

    async updateProfile(payload) {
        await this.auth.refreshTokenIfNeeded();
        try {
            // console.log('Updating profile:', payload);
            const response = await this.apiR7eClient.put(`account/${this.account_type}/profile/`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating profile:', error.response ? error.response.data : error.message);
            return null;
        }
    }

    /**
     * Get API keys for the current account.
     * @returns {Promise<Array>|null}
     */
    async getAccountApiKeys() {
        await this.auth.refreshTokenIfNeeded();
        try {
            const response = await this.apiR7eClient.get(`account/${this.account_type}/api-keys/`);
            return response.data.keys;
        } catch (error) {
            console.error("Error fetching API keys:", error.response ? error.response.data : error.message);
            return null;
        }
    }

    /**
     * Create a new API key for the current account.
     * @param {Object} payload 
     * @returns {Promise<Object|null>}
     */
    async createAccountApiKey(payload) {
        await this.auth.refreshTokenIfNeeded();
        try {
            const formData = new FormData();
            formData.append('name', payload.name);
            const response = await this.apiR7eClient.post(`account/${this.account_type}/api-keys/`, formData);
            return response.data;
        } catch (error) {
            console.error("Error creating API key:", error.response ? error.response.data : error.message);
            return null;
        }
    }

    /**
     * Change the account password.
     * @param {string} currentPassword - Current password for verification
     * @param {string} newPassword - New password
     * @returns {Promise<Object|null>}
     */
    async changePassword(currentPassword, newPassword) {
        await this.auth.refreshTokenIfNeeded();
        try {
            const response = await this.apiR7eClient.post(`account/${this.account_type}/change-password/`, {
                current_password: currentPassword,
                new_password: newPassword
            });
            return response.data;
        } catch (error) {
            console.error("Error changing password:", error.response ? error.response.data : error.message);
            throw error;
        }
    }

    /**
     * Request a password reset email. Sends a reset link to the user's registered email.
     * The user can then set a new password via the link.
     * @returns {Promise<Object>}
     */
    async requestPasswordReset() {
        await this.auth.refreshTokenIfNeeded();
        try {
            const response = await this.apiR7eClient.post(`account/${this.account_type}/request-password-reset/`, {});
            return response.data;
        } catch (error) {
            console.error("Error requesting password reset:", error.response ? error.response.data : error.message);
            throw error;
        }
    }

    /**
     * Request a change of primary email. Sends a verification email to the new address.
     * The new email is activated only after the user verifies it.
     * @param {string} newEmail - New primary email address
     * @returns {Promise<Object|null>}
     */
    async changePrimaryEmail(newEmail) {
        await this.auth.refreshTokenIfNeeded();
        try {
            const response = await this.apiR7eClient.post(`account/${this.account_type}/change-email/`, {
                new_email: newEmail
            });
            return response.data;
        } catch (error) {
            console.error("Error changing email:", error.response ? error.response.data : error.message);
            throw error;
        }
    }

    // --- Account API Methods ---

    async logoutAccount() {
        if (DEBUG_MODE) {
            console.log('R7E_IBaseApp, logoutAccount', this.auth);
        }
        await this.auth.logoutAccount();
        return true;
    }

    logoutAccountSync() {
        if (DEBUG_MODE) {
            console.log('R7E_IBaseApp, logoutAccountSync', this.auth);
        }
        this.auth.logoutAccountSync();
        return true;
    }

    /**
     * Log in with username and password, ensuring the account has the correct type.
     * @param {string} username 
     * @param {string} password 
     * @returns {Promise<Object>} Account data
     * @throws {R7eAccountTypeError}
     */    
    async loginAccount(username, password) {
        const userid = await this.auth.login(
            username,
            password,
            this.account_type,
            userData => userData['is_' + this.account_type] || false
        );
        if (!userid) {
            throw new R7eAccountTypeError(`Could not find a ${this.account_type} account`);
        }
        const userAccount = await this.getAccount();
        // console.log('R7E_IBaseApp, loginAccount', userAccount);
        this.auth.authData.roles = [];
        if (userAccount) {
            if (userAccount?.name) {
                this.auth.authData.name = userAccount.name;
            } else if (userAccount?.data?.profile?.name) {
                this.auth.authData.name = userAccount.data.profile.name;
            }
            for (const role of this.auth.availableRoles) {
                if (userAccount['is_' + role]) {
                    this.auth.authData.roles.push(role);
                }
            }
            this.auth.authData.pendingRegistrations[this.account_type] = userAccount.has_pending_registration;
            if (userAccount['is_' + this.account_type] || false) {
                this.auth.updateAccountRoles(this.auth.authData.roles);
                this.auth.updateAccountStorage(this.auth.authData);
                // console.log('R7E_IBaseApp, loginAccount => ', userAccount, this.auth.authData);
                return userAccount;
            } else {
                // Gather available roles for error reporting
                const ua_roles = [];
                for (const role of this.auth.availableRoles) {
                    if (userAccount['is_' + role]) {
                        ua_roles.push(role);
                    }
                }
                throw new R7eAccountTypeError(
                    `This account is not a ${this.account_type} account. Please contact support if you believe this is an error.`,
                    ua_roles
                );
            }
        }
        // console.log('R7E_IBaseApp, loginAccount, userid = ', userid);
        return userid;
    }

    async refreshAccount() {
        // console.log('R7E_IBaseApp, refreshAccount', this.account_type, this.baseAppType );
        try {
            const userAccount = await this.getAccount();
            const roles = [];    
            if (userAccount) {
                if (DEBUG_MODE) {
                    console.log('R7E_IBaseApp, refreshAccount userAccount = ', userAccount);
                }
                for (const role of this.auth.availableRoles) {
                    if (userAccount['is_' + role]) {
                        roles.push(role);
                    }
                }
                this.auth.authData.pendingRegistrations[this.account_type] = userAccount.has_pending_registration;
                if (userAccount?.name) {
                    this.auth.authData.name = userAccount.name;
                } else if (userAccount?.data?.profile?.name) {
                    this.auth.authData.name = userAccount.data.profile.name;
                }
                if (userAccount?.email) {
                    this.auth.authData.setEmail(userAccount.email);
                }

                if (userAccount ['is_' + this.account_type] || false) {
                    this.auth.updateAccountRoles(roles);
                }         
                this.auth.updateAccountStorage(this.auth.authData, userAccount?.data?.profile);
                // console.log('R7E_IBaseApp, refreshAccount authData = ', this.auth.authData);
                return userAccount;
            } else {
                // console.log('R7E_IBaseApp, refreshAccount', 'no userAccount');
                // TODO check if server is still reachable
                let server_reachable = false;
                try {
                    const response = await this.apiR7eClient.get('');
                    if (response.status === 200) {
                        server_reachable = true;
                    }
                } catch (error) {
                    console.error('Error checking server reachability:', error.response ? error.response.data : error.message);
                }
                if (server_reachable) {
                    /* Server is still reachable, so logout if not account is found */
                    this.auth.logoutAccount();
                } else {
                    /* Server is not reachable, so throw error */
                    throw new Error('Server is not reachable');
                }
                return null;
            }
        } catch (error) {
            console.error('Error refreshing account:', error.response ? error.response.data : error.message);
            return null;
        }
    }

    /**
     * Log in using a magic link.
     * @param {string} uid 
     * @param {string} username 
     * @param {string} token 
     * @param {string} refresh 
     * @returns {Promise<Object|null>}
     */

    async loginAccountWithMagicLink(uid, username, token, refresh) {
        // Login with magic link and verify account type
        if (DEBUG_MODE) {
            console.log('R7E_IBaseApp, loginAccountWithMagicLink', uid, username, token, refresh);
            console.log('R7E_IBaseApp, loginAccountWithMagicLink this.auth.authData = ', this.auth.authData);
        }
        try {
            const userid = await this.auth.loginAccountWithMagicLink(uid, username, token, refresh);
            if (!userid) return null;
            const userAccount = await this.getAccount();
            let prof_name = null;
            if (userAccount?.name) {
                prof_name = userAccount.name;
            } else if (userAccount?.data?.profile?.name) {
                prof_name = userAccount.data.profile.name;
            } else if (userAccount?.email) {
                prof_name = userAccount.email;                
            }
            if (prof_name) {
                this.auth.authData.name = prof_name;
                this.auth.authData.setName(prof_name);
            }
            
            const roles = [];
            if (userAccount) {
                for (const role of this.auth.availableRoles) {
                    if (userAccount['is_' + role]) {
                        roles.push(role);
                    }
                }
                if (userAccount['is_' + this.account_type] || false) {
                    this.auth.updateAccountRoles(roles);
                }
                this.auth.updateAccountStorage(this.auth.authData);
                return userAccount;
            }
            return userid;
        } catch (error) {
            console.error('Error logging in with magic link:', error.response ? error.response.data : error.message);
            return null;
        }
    }

    /**
     * Request a sign-in email for the account.
     * @param {Object} payload 
     * @returns {Promise<Object>}
     */    
    async requestSignInWithAccountEmail(payload) {
            // Request sign-in with email (magic link)
        // console.log('Requesting sign in with email:', payload);
        const response = await this.apiR7eClient.post(`${this.account_type}/sign-in-with-email/`, payload);
        // console.log('Sign in with email response:', response.data);
        return response.data;
    }


    /**
     * Request a sign magic link-in email for the account.
     * @param {Object} payload 
     * @returns {Promise<Object>}
     */    
    async requestSignMagicLink(payload) {
            // Request sign magic link with authentication
        // console.log('Requesting sign magic link:', payload);
        const response = await this.apiR7eClient.post(`account/${this.account_type}/sign-magic-link/`, payload);
        // console.log('Sign magic link response:', response.data);
        return response.data;
    }

    /**
     * Register a new account.
     * @param {Object} payload 
     * @returns {Promise<Object>}
     */
    async registerAccount(payload) {
        try {
            if (DEBUG_MODE) {
                console.log('=== REGISTRATION REQUEST DEBUG ===');
                console.log('Account type:', this.account_type);
                console.log('Endpoint:', `${this.account_type}/register/`);
                console.log('Full endpoint URL:', this.apiR7eClient.defaults.baseURL + `${this.account_type}/register/`);
                console.log('Payload:', payload);
                console.log('===================================');
            }
            const response = await this.apiR7eClient.post(`${this.account_type}/register/`, payload);
            if (DEBUG_MODE) {
                console.log('Registration response:', response.data);
            }
            return response.data;
        } catch (error) {
            if (DEBUG_MODE) {
                console.error("=== REGISTRATION API ERROR ===");
                console.error("Full error:", error);
                console.error("Error response:", error.response ? error.response.data : error.message);
                console.error("Status:", error.response?.status);
                console.error("Status text:", error.response?.statusText);
                console.error("===============================");
            }
            throw error;
        }
    }  
    
    /**
     * Resend activation email. Not implemented.
     * @param {string} email 
     * @throws {Error}
     */
    async resendActivationEmail(email) {
        try {
            const response = await this.apiR7eClient.post(`${this.account_type}/register/resend_email/`, { email: email });
            return response.data;
        } catch (error) {
            console.error("Error resending activation email:", email, error.response ? error.response.data : error.message);
            throw error;
        }
    }      

    /**
     * Subscribe an account to a service.
     * @param {string} account_type 
     * @returns {Promise<Object>}
     */
    async subscribeAccount(account_type) {
        // Subscribe account to a service
        if (!this.isAuthenticated()) {
            throw new Error("Not authenticated");
        }
        try {
            let data=null;
            const response = await this.apiR7eClient.post(
                `${account_type}/subscribe/`,
                { service: account_type }
            );
            if (response && response.data) {
                data = response.data;
            }
            if (data && data.status === 'success') {
                if (data['is_' + account_type]) {
                    this.auth.updateAccountRoles([account_type]);
                    this.auth.updateAccountStorage(this.auth.authData);
                }
                return data;
            } else {
                throw new Error("Failed to subscribe account");
            }   
        } catch (error) {
            throw new Error("Failed to subscribe account: " + error.message);
        }
    }  

    /**
     * Check if an account is activated.
     * @param {string} email 
     * @param {string} password 
     * @returns {Promise<Object>}
     */
    async checkAccountActivation(email, reg_token) {
        try {

            const payload = {
                email: email,
                registration_token: reg_token
            };
            const response = await this.apiR7eClient.post(`${this.account_type}/register/status/`, payload);
            // const userData = response.data;
            // console.log('checkAccountActivation response:', response.data);
            const data = response.data;
            if (data.status === 'accepted') {
                return { status: data.status, activated: true };
            } else {
                return { status: data.status, activated: false };
            }

            // const hasRequiredRole = userData['is_' + this.account_type] || false;
            // const hasJwtLink = userData._links && userData._links['jwt:access_token'];
            // return {
            //     activated: hasJwtLink && hasRequiredRole,
            //     hasRequiredRole,
            //     hasJwtLink,
            //     userData
            // };
        } catch (error) {
            console.error("Error checking account activation:", error);
            if (error.response?.status === 401) {
                return { status: 'error', activated: false, error: 'Invalid credentials' };
            }
            throw error;
        }
    }

    /**
     * Get notification messages.
     * @param {number} offset 
     * @param {number} count 
     * @param {string} include 
     * @returns {Promise<Object>}
     */
    async getNotificationMessages(offset = 0, count = 25, include = 'unread') {
        let include_query = '';
        if (include == 'all') {
            include_query = 'all';
        }
        const response = await this.apiR7eClient.get(`account/${this.account_type}/notifications/`, { params: { offset: offset, count: count, include: include_query, channel: 'web' } });
        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error("Failed to get notification messages");
        }
    }

    async markNotificationAsRead(id) {
        const response = await this.apiR7eClient.put(`account/${this.account_type}/notifications/${id}/`, { read: true, channel: 'web' });
        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error("Failed to mark notification as read");
        }
    }

    async markNotificationAsUnread(id) {
        const response = await this.apiR7eClient.put(`account/${this.account_type}/notifications/${id}/`, { read: false, channel: 'web' });
        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error("Failed to mark notification as unread");
        }
    }

    // --- Role and Locale Utilities ---
    availableRoles() { return this.auth.availableRoles; }

    getLocale() { return localStorage.getItem('locale') || 'en'; }
    setLocale(locale) { localStorage.setItem('locale', locale); }
    removeLocale() { localStorage.removeItem('locale'); }
    getAvailableLocales() { return ['en', 'fr', 'de', 'es', 'it', 'ja', 'zh']; }

    getExperimentationsKey() { return LocalStoragePrefix + 'experimentations.' + this.account_type; }
    getAvailableExperimentations() { return []; }
    getExperimentations() { 
        const value = localStorage.getItem(this.getExperimentationsKey());
        if (!value || value === 'false' || value === '') {
            return false;
        }
        return value;
    }
    setExperimentations(experimentations) { 
        if (!experimentations || experimentations === '' || experimentations === 'false') {
            localStorage.removeItem(this.getExperimentationsKey());
        } else {
            localStorage.setItem(this.getExperimentationsKey(), typeof experimentations === 'string' ? experimentations : JSON.stringify(experimentations));
        }
    }
    removeExperimentations() { localStorage.removeItem(this.getExperimentationsKey()); }

    // --- Service URL Utilities ---
    getCMSUrl(suffix = null) { return this.auth.getCMSUrl(suffix); }
    getPublicContentCMSUrl(contentId) { 
        return this.service_url('publisher') + 'store/content/' + contentId; 
    }
    getClaimContentURL(claimId) {
        return this.service_url('author') + 'contents/' + claimId;
    }

    /**
     * Returns the service URL for a given service name.
     * Falls back to portal/website or CMS URL if not found in env.
     */
    service_url(service) {
        const envUrl = process.env[`VUE_APP_${service.toUpperCase()}_CMS_URL`];
        if (envUrl) {
            return envUrl;
        }
        if (service === 'portal') {
            return this.getCMSUrl();
        }
        if (service === 'website') {
            return 'https://www.recognyze.ai';
        }
        return this.getCMSUrl(service + '/');
    }

    /**
     * Checks if a service is available by attempting to fetch its root URL with fetch() in 'no-cors' mode.
     */
    async is_service_available(service) {
        const url = this.service_url(service);
        if (!url) return false;
        const testUrl = url.replace(/\/$/, '') + '?_cb=' + Date.now();
        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 1500);
            await fetch(testUrl, { mode: 'no-cors', signal: controller.signal });
            clearTimeout(timeout);
            return true;
        } catch (e) {
            return false;
        }
    }

    // --- Account Validation ---
    async check_valid_account() {
        // Checks if the current account is valid and has the required role
        // console.log('R7E_IBaseApp, check_valid_account', this.baseAppType, this.isAuthenticated());
        if (this.isAuthenticated()) {
            try {
                await this.refreshAccount();
                return { isAuthenticated: this.isAuthenticated(), hasAccessToService: this.hasAccessToService(this.account_type) };
            } catch (e) {
                // this.logout();
                // console.error('Error checking valid account:', e);
                return { isAuthenticated: false, hasAccessToService: false, error: true };
            }
        }
        return { isAuthenticated: false, hasAccessToService: false };
    }

    // --- Authenticated State ---
    isAuthenticated() { return this.auth.isAuthenticated(); }
    // hasPendingRegistrations() { return this.auth.hasPendingRegistrations(); }
    hasPendingRegistrationForCurrentService() { 
        // console.log('R7E_IBaseApp, hasPendingRegistrationForCurrentService ...', this.account_type, this.getAuthData().pendingRegistrations);
        return this.auth.hasPendingRegistration(this.account_type); 
    }
    hasPendingRegistration(srv) { return this.auth.hasPendingRegistration(srv); }

    /* Invitation and waiting list settings */

    async checkInvitationCode(code, product) { return this.auth.checkInvitationCode(code, product); }
    async subscribeToWaitingList(email, product) { return this.auth.subscribeToWaitingList(email, product); }

    minWaitingListTotal() { return r7eSettings.waitingList.minTotal; }
    isRegistrationByInvitationEnabled(service='all') { return r7eSettings.registration[service].registrationByInvitationEnabled; }
    isRegistrationByWaitingListEnabled(service='all') { return r7eSettings.registration[service].registrationByWaitingListEnabled; }
    isRegistrationByEmailEnabled(service='all') { return r7eSettings.registration[service].registrationByEmailEnabled; }

    async getAccountServiceSettings(service) {
        const defaults = {
          isRegistrationByInvitationEnabled: r7eSettings.registration[service].registrationByInvitationEnabled,
          isRegistrationByWaitingListEnabled: r7eSettings.registration[service].registrationByWaitingListEnabled,
          isRegistrationByEmailEnabled: r7eSettings.registration[service].registrationByEmailEnabled
        };
        try {
          const { data } = await this.apiR7eClient.get(`${service}/settings/registration/`);
          return {
            isRegistrationByInvitationEnabled: data?.isRegistrationByInvitationEnabled ?? defaults.isRegistrationByInvitationEnabled,
            isRegistrationByWaitingListEnabled: data?.isRegistrationByWaitingListEnabled ?? defaults.isRegistrationByWaitingListEnabled,
            isRegistrationByEmailEnabled: data?.isRegistrationByEmailEnabled ?? defaults.isRegistrationByEmailEnabled
          };
        } catch (e) {
          return defaults;
        }
      }    

    /* Setup pages */
    setupPages() { return ["/profile-setup"]; }
}

/**
 * R7E_IApplication
 * Interface/wrapper for account-type-specific application logic.
 */
class R7E_IApplication {
    baseAppType = null;

    constructor(kind, app) {
        if (!app) {
            throw new Error('R7E_IApplication requires an app instance to be provided to the constructor.');
        }
        this.account_type = kind;
        this.base = app;
        this.base.account_type = this.account_type;
        this.baseAppType = "Application::"+ this.base.baseAppType;
        this.base.setMainApp(this);
    }

    // --- Delegate Methods to Base App ---
    loadCredentials() { return this.base.loadCredentials(); }
    isAuthenticated() { return this.base.isAuthenticated(); }
    hasPendingRegistrations() { return this.base.hasPendingRegistrations(); }
    hasPendingRegistration(srv) { return this.base.hasPendingRegistration(srv); }
    hasPendingRegistrationForCurrentService() { return this.base.hasPendingRegistrationForCurrentService(); }
    hasAccessToService(service) { return this.base.hasAccessToService(service); }
    hasAccessToCurrentService() { return this.base.hasAccessToCurrentService(); }
    async clearCredentials() { return this.base.clearCredentials(); }
    registerCredentialsObserver(cb) { return this.base.registerCredentialsObserver(cb); }
    unregisterCredentialsObserver(cb) { return this.base.unregisterCredentialsObserver(cb); }
    async loginAccount(username, password) { return this.base.loginAccount(username, password); }
    async loginAccountWithMagicLink(uid, username, token, refresh) { return this.base.loginAccountWithMagicLink(uid, username, token, refresh); }
    async logoutAccount() { this.base.logoutAccount(); }
    logoutAccountSync() { this.base.logoutAccountSync(); }
    async requestAccountRefresh() { 
        await this.base.refreshAccount(); 
    }
    async check_valid_account() { return this.base.check_valid_account(); }
    async getAccount() { return this.base.getAccount(); }
    async requestSignInWithAccountEmail(payload) { return this.base.requestSignInWithAccountEmail(payload); }
    async requestSignMagicLink(payload) { return this.base.requestSignMagicLink(payload); }
    async getProfile() { return this.base.getProfile(); }
    async updateProfile(payload) { return this.base.updateProfile(payload); }
    async getNotificationMessages(offset = 0, count = 25, include = '') { return this.base.getNotificationMessages(offset, count, include); }
    async markNotificationAsRead(id) { return this.base.markNotificationAsRead(id); }
    async markNotificationAsUnread(id) { return this.base.markNotificationAsUnread(id); }
    getUsername() { return this.base.getUsername(); }
    getDisplayName() { return this.base.getDisplayName(); }
    getProfileName() { return this.base.getDisplayName(); }
    has_role(role) { return this.base.has_role(role); }
    hasAuthorRole() { return this.base.hasAuthorRole(); }
    hasPublisherRole() { return this.base.hasPublisherRole(); }
    getLocale() { return this.base.getLocale(); }
    getAvailableLocales() { return this.base.getAvailableLocales(); }
    setLocale(locale) { return this.base.setLocale(locale); }
    removeLocale() { return this.base.removeLocale(); }
    availableRoles() { return this.base.availableRoles(); }

    getExperimentations() { return this.base.getExperimentations(); }
    getAvailableExperimentations() { return this.base.getAvailableExperimentations(); }
    setExperimentations(experimentations) { return this.base.setExperimentations(experimentations); }
    removeExperimentations() { return this.base.removeExperimentations(); }

    isFeatureAvailable(feature) { /* eslint-disable-line no-unused-vars */
        // TODO: add feature flag management
        if (this.getExperimentations()) {
            return this.getExperimentations() === 'true' 
                || this.getExperimentations().includes(feature)
                || this.getExperimentations().includes('all');
        }
        return false;
    }

    service_url(account_type) { return this.base.service_url(account_type); }
    is_service_available(service) { return this.base.is_service_available(service); }
    getPublicContentCMSUrl(contentId) { return this.base.getPublicContentCMSUrl(contentId); }
    getClaimContentURL(claimId) { return this.base.getClaimContentURL(claimId); }
    getCacheManager() { return this.base.getCacheManager(); }
    getDebugManager() { return this.base.getDebugManager(); }    
    isDebugMode() { return this.base.isDebugMode(); }
    isConsoleLogEnabled() { return this.base.isConsoleLogEnabled(); }
    setConsoleLogEnabled(b) { return this.base.setConsoleLogEnabled(b); }
    isUIDebugEnabled() { return this.base.isUIDebugEnabled(); }
    setUIDebugEnabled(b) { return this.base.setUIDebugEnabled(b); }
    isDebugModeVisible() { return this.base.isDebugModeVisible(); }
    setDebugModeVisible(b) { return this.base.setDebugModeVisible(b); }
    isUsingFakeData() { return this.base.isUsingFakeData(); }
    setUsingFakeData(b) { return this.base.setUsingFakeData(b); }

    log(...args) {
        if (this.isConsoleLogEnabled()) {
            console.log(...args);
        }
    }

    /* UI settings */
    setUILayout(layout) { this.base.setUILayout(layout); }
    getUILayout() { return this.base.getUILayout(); }
    useSidebar() { return this.getUILayout() === 'sidebar'; }
    useNavbar() { return this.getUILayout() === 'navbar'; }
    getNavBarData() { return new R7E_NavBarData(this); }
    getHomeItems() { return []; }
    isHelpEnabled() { return this.base.isHelpEnabled(); }
    setHelpEnabled(b) { return this.base.setHelpEnabled(b); }

    /* Profile fields settings */
    getProfileFieldsSettings() {
        const profile_fields = [];
        profile_fields.push(
            { key: 'name', label: 'displayName', type: 'text', required: false }
        )
        profile_fields.push(
            { key: 'affiliation', label: 'affiliation', type: 'text', required: false }
        )
        profile_fields.push(
            { key: 'home_page', label: 'homePage', type: 'url', required: false,
              validation: (value) => {
                return this.isValidUrl(value);
              },
              errorMessage: 'Invalid Home Page'
            }
        )
        profile_fields.push(
            { key: 'emails', label: 'secondaryEmails', type: 'email', required: false }
        )
        return profile_fields;
    }

    setupPages() { return this.base.setupPages(); }

     /****************************************
     * Invitation and waiting list settings *
     ****************************************/

    /**
     * Check if an invitation code is valid.
     * @param {string} code - The invitation code to validate
     * @param {string} [product] - Product name (publisher, aggregator, author, recognyze). Uses current app context if not provided.
     * @returns {Promise<{valid: boolean, product?: string, email?: string, used?: boolean, used_at?: string, created_at?: string, expires_at?: string, max_uses?: number, use_count?: number}>}
     */
    async checkInvitationCode(code, product) { 
        return this.base.checkInvitationCode(code, product);
    }
    /**
     * Subscribe an email to the waiting list.
     * @param {string} email - Email to subscribe
     * @param {string} product - Product name (publisher, aggregator, author, recognyze). Required.
     * @returns {Promise<{subscribed: boolean, order?: number, total?: number, email?: string}>}
     */
    async subscribeToWaitingList(email, product) { 
        return this.base.subscribeToWaitingList(email, product);
    }
    minWaitingListTotal() { return this.base.minWaitingListTotal(); }
    isRegistrationByInvitationEnabled(service='all') { return this.base.isRegistrationByInvitationEnabled(service); }
    isRegistrationByWaitingListEnabled(service='all') { return this.base.isRegistrationByWaitingListEnabled(service); }
    isRegistrationByEmailEnabled(service='all') { return this.base.isRegistrationByEmailEnabled(service); }
    getAccountServiceSettings(service) { return this.base.getAccountServiceSettings(service); }

    /* Utilities */

    isValidUrl(url) { return this.base.isValidUrl(url); }
    isValidEmail(email) { return this.base.isValidEmail(email); }
    formatAmount(amount, currency, precision = 2) { return this.base.formatAmount(amount, currency, precision); }
    formatNumber(n) { return this.base.formatNumber(n); }
    formatFloat(n, precision = 2) { return this.base.formatFloat(n, precision); }
    formatDateTime(dateString) { return this.base.formatDateTime(dateString); }
}

/**
 * R7E_BaseApp
 * 
 * Main application service for authentication, account, and service utilities.
 * Extends R7E_IBaseApp with CMS authentication.
 */
class R7E_BaseApp extends R7E_IBaseApp {
    constructor() {
        super(new R7E_CmsAuth());
        this.baseAppType = 'cms';
    }
}

// Singleton instance for shared use
const r7eBaseApp = new R7E_BaseApp();

export { R7E_IBaseApp, R7eAccountTypeError, R7E_IApplication, R7E_BaseApp, r7eBaseApp, API_URL, DEBUG_MODE, R7E_NavBarData, R7E_HomeItem, R7E_DebugManager, R7E_CacheManager };
